import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";

import Styles from "./Navbar.module.css";
import { ReactComponent as Gna } from "../../assets/images/logo.svg.svg";
import { publicEnergyType, publicRating } from "../../Redux/Slices/publicSlice";
import { userAccess } from "../../Redux/Slices/profileSlice";
import NavProfile from "./NavProfile";
import MobileNavbar from "./MobileNavbar";
import NotificationContainer from "./NotificationContainer/NotificationContainer";

// ─── Constants ───────────────────────────────────────────────────────────────

const ADMIN_ROLES = ["MAKER", "CHECKER", "ADMIN", "SUPER_ADMIN"];

const navList = {
  CONSUMER: [
    { name: "Home", url: "dashboard" },
    { name: "Bilateral Ledger", url: "bilateral" },
    { name: "Auction", url: "auction" },
    { name: "Search Listing", url: "search_listing" },
    { name: "Landing Cost Calculator", url: "lcc" },
    { name: "Past Listing", url: "past_listing_con_gen" },
    { name: "RFQ", url: "rfq" },
    { name: "TradeConnect", url: "tradeconnect" },
    { name: "REC", url: "rec" },
    { name: "BESS", url: "bess" },
    { name: "VPPA", url: "vppa" },

    { name: "Reverse Auction", url: "reverse_auction" },
    { name: "Tender Details", url: "tender_active" },
  ],
  GENERATOR: [
    { name: "Home", url: "dashboard" },
    { name: "Ledger", url: "bilateral" },
    { name: "Auction", url: "auction" },
    { name: "Search Listing", url: "search_listing" },
    { name: "Landing Cost Calculator", url: "lcc" },
    { name: "Past Listing", url: "past_listing_con_gen" },
    { name: "RFQ", url: "rfq" },
    { name: "TradeConnect", url: "tradeconnect" },
    { name: "VPPA", url: "vppa" },
    { name: "Reverse Auction", url: "reverse_auction" },
    { name: "BESS", url: "bess" },
    { name: "REC", url: "rec" },

    { name: "Tender Details", url: "tender_active" },
  ],
  BANKING: [
    { name: "Home", url: "banking" },
    { name: "Search Listing", url: "banking_search" },
    { name: "My Banking Ledger", url: "banking_ledger" },
  ],
  COMPANY_ADMIN: [],
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const EntityTitle = ({ role, activeSidebarItem }) => {
  const persistData = useSelector((s) => s?.persistedReducer);
  const { activeConsumerId, activeGeneratorId, activeDiscomId } =
    persistData?.user ?? {};

  const entityName = activeConsumerId
    ? persistData?.consumer?.find((e) => e.consumerId === activeConsumerId)
        ?.consumerName
    : activeGeneratorId
      ? persistData?.generator?.find((e) => e.generatorId === activeGeneratorId)
          ?.generatorName
      : activeDiscomId
        ? persistData?.discom?.find((e) => e.discomId === activeDiscomId)
            ?.discomName || persistData?.company?.displayName
        : "";

  return (
    <div>
      <div style={{ color: "#6F6D7C", fontWeight: "600" }}>{entityName}</div>
      <div
        style={{
          marginTop: "2px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "14px",
          color: role === "GENERATOR" ? "#34656D" : "#f1935c",
          fontWeight: "500",
        }}
      >
        <span>{activeSidebarItem}</span>
        {role && role !== "BANKING" ? (
          <>
            <span>-</span>
            <span>{role === "GENERATOR" ? "Sell" : "Buy"}</span>
          </>
        ) : role === "BANKING" ? (
          <div> - Banking</div>
        ) : null}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const Navbar = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const token = useSelector((s) => s?.persistedReducer?.token);
  const role = useSelector((s) => s?.persistedReducer?.user?.activeRole);
  const activeEntity = useSelector((s) => s?.activeEntity?.activeEntity);
  const activeSidebarItem = useSelector(
    (s) => s?.activeEntity?.activeSidebarItem,
  );
  const profile = useSelector((s) => s?.profileSlice);
  const persistData = useSelector((s) => s?.persistedReducer);
  const companyName = persistData?.company?.companyName;
  const displayName = persistData?.company?.displayName;
  const isUserType = persistData?.userType;

  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    if (token && !profile.userAccess) dispatch(userAccess(token));
    dispatch(publicRating());
    dispatch(publicEnergyType());
  }, [token, profile.userAccess, dispatch]);

  const getDashboardTitle = ({ listName }) => {
    const path = location.pathname;

    if (path.includes("/my_profile"))
      return (
        <div className={Styles.sideItems}>
          <span>{companyName}</span>
          <span
            style={{ color: "#f1935c", fontWeight: "400", fontSize: "14px" }}
          >
            My Profile
          </span>
        </div>
      );
    if (path.includes("/wallet"))
      return (
        <div className={Styles.sideItems}>
          <span>{activeEntity}</span>
          <span
            style={{ color: "#f1935c", fontWeight: "400", fontSize: "14px" }}
          >
            {" "}
            Escrow
          </span>
        </div>
      );
    if (path.includes("/archive"))
      return (
        <div className={Styles.sideItems}>
          <span>{activeEntity || displayName}</span>
          <span
            style={{ color: "#f1935c", fontWeight: "400", fontSize: "14px" }}
          >
            {" "}
            Archive
          </span>
        </div>
      );
    if (path.includes("/unified_ledger"))
      return (
        <div className={Styles.sideItems}>
          <span>{activeEntity}</span>
          <span
            style={{ color: "#f1935c", fontWeight: "400", fontSize: "14px" }}
          >
            {" "}
            Unified Ledger
          </span>
        </div>
      );
    if (path.includes("/coming-soon")) return "OTC Dashboard - Coming Soon";

    if (role === "BANKING")
      return (
        <EntityTitle
          role="BANKING"
          activeSidebarItem={activeSidebarItem || listName}
        />
      );
    if (role === "CONSUMER" || role === "GENERATOR")
      return (
        <EntityTitle
          role={role}
          activeSidebarItem={activeSidebarItem || listName}
        />
      );

    return (
      <>
        {isUserType === "COMPANY_ADMIN" && (
          <div className={Styles.sideItems}>
            <span>{companyName}</span>
            <span
              style={{ color: "#f1935c", fontWeight: "400", fontSize: "14px" }}
            >
              OTC Dashboard
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {drawer && (
        <MobileNavbar open={drawer} handleClose={() => setDrawer(false)} />
      )}
      <div className={Styles.navbar_section}>
        {/* Desktop */}
        <div className={Styles.navbar_sub_container}>
          <div className={Styles.navbar_logo_section}>
            <div className={Styles.navabr_logo}>
              <Gna />
            </div>
            <div className={Styles.dashboardTitle}>
              {getDashboardTitle({
                listName: navList[role]?.find(
                  (e) => `/${e.url}` === location.pathname,
                )?.name,
              })}
            </div>
          </div>
          <div className={Styles.navbar_container}>
            {token && !ADMIN_ROLES.includes(role) && <NotificationContainer />}
            {token && <NavProfile />}
          </div>
        </div>

        {/* Mobile */}
        <div className={Styles.navbar_sub_mobile_container}>
          <div className={Styles.navbar_logo_main_section}>
            <div
              className={Styles.menuContainer}
              onClick={() => setDrawer(true)}
            >
              <MenuIcon />
            </div>
            <div className={Styles.navabr_logo}>
              <Gna />
            </div>
          </div>
          <div className={Styles.currentPageName}>
            {
              navList[role]?.find((e) => `/${e.url}` === location.pathname)
                ?.name
            }
          </div>
          {token && <NavProfile />}
        </div>
      </div>
    </>
  );
};

export default Navbar;
